import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { Link, Card, Text } from '@/components/common'
import Form from '@/components/forms/Form'
import InputField from '@/components/forms/InputField'
import { Column, Row } from '@/components/layout'
import usePathnameChange from '@/hooks/usePathnameChange'
import Yup from '@/utils/yup'
import { sendError } from '@/utils/rollbar'
import CheckboxField from '@/components/forms/CheckboxField'

import styles from './TemplateDownloadForm.module.scss'

const FormResetter = () => {
  const { reset } = useFormContext()
  usePathnameChange(reset)
  return null
}

const templateData = {
  balanceSheet: { label: 'Balance Sheet Template' },
  incomeStatement: { label: 'Income Statement Template' },
  invoice: { label: 'Invoice & Payment Request Templates', source: 'Invoice & Payment Request Template' },
}

export default function TemplateDownloadForm({ downloadLink, templateType }) {
  const [succeeded, setSucceeded] = useState(false)
  const { formatMessage } = useIntl()

  const schema = Yup.object({
    email: Yup.string().trim().lowercase().email()
      .required(),
    agreedToTerms: Yup.bool().required().oneOf([true], 'Please consent to receiving marketing communications from The Neat Company.'),
  })

  const templateLabel = templateData[templateType]?.label ?? 'template'

  const handleSubmit = async (model) => {
    const source = templateData[templateType]?.source ?? templateLabel
    const values = schema.cast(model)
    try {
      await fetch('/api/lead/create', {
        method: 'POST',
        body: JSON.stringify({ email: values.email, source }),
      })
    } catch (error) {
      sendError(error, `Failed to create lead: ${source}`)
    }

    setSucceeded(true)
  }

  return (
    <div className={styles.container}>
      {succeeded ? (
        <Card intent="success">
          <Text.P
            messageProps={{
              id: 'templates.success',
              values: { templateType: templateLabel, templateCount: templateType === 'invoice' ? 2 : 1 },
            }}
          />
          <Link to={downloadLink}>Click here to download</Link>
        </Card>
      ) : (
        <Form
          initialValues={{ email: '', agreedToTerms: false }}
          onSubmit={handleSubmit}
          options={{ mode: 'all' }}
          schema={schema}
        >
          <Row>
            <Column>
              <InputField
                autoComplete="email"
                ignoreTouched={false}
                indicateValid
                label={formatMessage({ id: 'templates.emailLabel' }, { templateType: templateLabel })}
                name="email"
                placeholder="Email"
                size="large"
              />
            </Column>
          </Row>
          <Row>
            <Column>
              <CheckboxField className={styles.checkboxField} label={formatMessage({ id: 'templates.checkboxLabel' })} name="agreedToTerms" />
            </Column>
          </Row>
          <Form.Action
            className={styles.submit}
            disabledIfInvalid
            messageId="cta.submit"
            size="large"
            submittingMessageId="cta.processing"
            type="submit"
            variant="primary"
          />
          <FormResetter />
        </Form>
      )}
    </div>
  )
}

TemplateDownloadForm.propTypes = {
  downloadLink: PropTypes.string,
  templateType: PropTypes.oneOf(['balanceSheet', 'incomeStatement', 'invoice']),
}
